import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import moment from 'moment';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import { PaginatedResult } from 'src/app/models/paginated-result.model';
import {
  CSVTicket,
  Ticket,
  TicketAggregate,
} from 'src/app/models/ticket/ticket.model';
import {
  isNonProactiveTicket,
  isTicketProactive,
} from 'src/app/models/ticket/ticket.util';
import { TicketService } from 'src/app/core/services/ticket';
import * as fromStore from 'src/app/store/';
import { CreateTicketComponent } from '../create-ticket/create-ticket.component';

@Component({
  selector: 'app-tickets-shell',
  templateUrl: './tickets-shell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class TicketsShellComponent implements OnInit, OnDestroy {
  ticketsValue: Ticket[] = [];

  loadingAggregateStats: boolean = false;

  isLoaded$: Observable<boolean> = this.store.select(
    fromStore.getTicketsLoading,
  );

  selectedTicket$: Observable<Ticket> = this.store.select(
    fromStore.getSelectedTicket,
  );

  browserSize$: Observable<string> = this.store.select(
    fromStore.getBrowserSizeName,
  );

  selectedTicket: Ticket = null;

  ticketAggregate: TicketAggregate | null = null;

  browserSize: string;

  ticketCsvData: CSVTicket[] = [];

  csvFileName = 'Tickets';

  page: number = 0;

  pageSize: number = 20;

  timeRange: string = 'all';

  readonly destroyRef = inject(DestroyRef);

  private destroy$ = new Subject<void>();

  searchTerm: string = '';

  isLoadingFilteredTickets: boolean = false;

  private params$ = new Subject<void>();

  // TODO: delete after Redis is ready
  timeRange$ = new BehaviorSubject<string>('all');

  baseTickets$!: Observable<PaginatedResult<Ticket>>;

  filteredTickets$!: Observable<PaginatedResult<Ticket>>;

  selectedTicketId: string | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private store: Store<fromStore.State>,
    private dialog: MatDialog,
    private ticketService: TicketService,
  ) {}

  ngOnInit(): void {
    this.browserSize$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((name) => {
        this.browserSize = name ?? this.browserSize;
        this.cdr.markForCheck();
      });

    // Fetch tickets whenever params (page, search, etc.) change
    this.baseTickets$ = this.params$.pipe(
      startWith(undefined),
      tap(() => {
        this.isLoadingFilteredTickets = true;
        this.cdr.markForCheck();
      }),
      switchMap(() =>
        this.ticketService.getTickets(
          this.page,
          this.pageSize,
          this.searchTerm,
          this.timeRange,
        ),
      ),
      shareReplay(1),
    );

    // Trigger initial data load
    this.params$.next();

    // Re-filter locally whenever timeRange$ changes
    this.filteredTickets$ = combineLatest([
      this.baseTickets$,
      this.timeRange$,
    ]).pipe(
      map(([result, range]) => {
        if (!result?.data?.length) return result;
        const cloned = result.data.map((t) => ({ ...t })); // break identity
        const filtered = this.filterByTimeRange(cloned, range);
        this.loadTicketStats(filtered);
        return { ...result, data: filtered, total: filtered.length };
      }),
      tap((result) => {
        // rebind selected ticket to the *fresh* instance
        if (this.selectedTicketId && result?.data?.length) {
          const updated = result.data.find(
            (t) => t.Id === this.selectedTicketId,
          );
          if (updated) {
            // ⬇️ Deep-clone the comments container + array so inputs actually change
            const next = {
              ...updated,
              Comments__r: updated?.Comments__r
                ? {
                    ...updated.Comments__r,
                    records: Array.isArray(updated.Comments__r.records)
                      ? [...updated.Comments__r.records]
                      : [],
                  }
                : null,
            };

            // Only assign if the reference (or comments length) is different
            if (
              this.selectedTicket !== next ||
              (this.selectedTicket?.Comments__r?.records?.length ?? -1) !==
                (next.Comments__r?.records?.length ?? -1)
            ) {
              this.selectedTicket = next;
              this.cdr.markForCheck();
            }
          }
        }

        if (!result?.data?.length) {
          this.selectedTicket = null;
          this.store.dispatch(new fromStore.SetSelectedTicket(null));
        }

        this.isLoadingFilteredTickets = false;
        this.cdr.detectChanges();
      }),
      shareReplay(1),
    );

    this.loadAllTicketsForCsv();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.cdr.detectChanges();
  }

  loadTickets(): void {
    this.params$.next();
  }

  onPaginationChanged(e: { page: number; pageSize: number }): void {
    this.page = e.page;
    this.pageSize = e.pageSize;
    this.params$.next();
  }

  loadTicketStats(tickets: Ticket[]): void {
    this.loadingAggregateStats = true;
    const aggregationResult = {
      total: 0,
      open: 0,
      closed: 0,
      proactive: 0,
      nonProactive: 0,
      finance: 0,
      support: 0,
      care: 0,
      maintenance: 0,
    };
    if (!tickets?.length) {
      this.ticketAggregate = aggregationResult;
      this.cdr.detectChanges();
      return;
    }

    aggregationResult.total = tickets.length;
    aggregationResult.open = tickets.filter((t) => !t.IsClosed).length;
    aggregationResult.closed = tickets.filter((t) => t.IsClosed).length;
    aggregationResult.proactive = tickets.filter((t) =>
      isTicketProactive(t),
    ).length;
    aggregationResult.nonProactive = tickets.filter((t) =>
      isNonProactiveTicket(t),
    ).length;

    this.ticketAggregate = aggregationResult;
    this.loadingAggregateStats = false;
    this.cdr.detectChanges();
  }

  loadTicketStatsRedis(): void {
    this.loadingAggregateStats = true;
    this.ticketService
      .getAggregateOpenTotal(this.timeRange)
      .pipe(takeUntil(this.destroy$))
      .subscribe((aggregationResult: TicketAggregate) => {
        this.ticketAggregate = aggregationResult;
        this.loadingAggregateStats = false;
        this.cdr.detectChanges();
      });
  }

  onSearchChanged(term: string): void {
    this.searchTerm = term;
    this.page = 0;
    this.params$.next();
  }

  onSelectTicket(t: Ticket): void {
    const id = t?.Id ?? null;
    this.selectedTicketId = id;
    // Treat missing/invalid ticket objects as null to keep state consistent
    this.selectedTicket = id ? t : null;
    this.cdr.detectChanges();
    this.store.dispatch(new fromStore.SetSelectedTicket(id));
  }

  openDialog(): void {
    // Dialog to create tickets
    const dialogConfig = {
      autoFocus: true,
      minHeight: this.browserSize === 'small' ? '315px' : '380px',
      width: this.browserSize === 'small' ? 'calc(100vw - 20px)' : '650px',
      maxWidth: '650px',
      data: {
        browserSize: this.browserSize,
      },
      disableClose: true,
    };

    const createTicketDialog = this.dialog.open(
      CreateTicketComponent,
      dialogConfig,
    );
    createTicketDialog
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.params$.next();
      });
  }

  onTimeRangeChanged(timeRange: string): void {
    this.timeRange$.next(timeRange || 'all');
    this.cdr.detectChanges();
  }

  filterByTimeRange(tickets: Ticket[], range: string): Ticket[] {
    if (!range || range === 'all') return tickets;

    const now = moment.utc();

    switch (range) {
      case '6hours': {
        const cutoff = now.clone().subtract(6, 'hours');
        return tickets.filter((t) =>
          moment.utc(t.CreatedDate).isSameOrAfter(cutoff),
        );
      }

      case '1day': {
        // Last 24 hours UTC
        const cutoff = now.clone().subtract(1, 'day');
        return tickets.filter((t) =>
          moment.utc(t.CreatedDate).isSameOrAfter(cutoff),
        );
      }

      case '1month': {
        // Last 30 days
        const cutoff = now.clone().subtract(30, 'days');
        return tickets.filter((t) =>
          moment.utc(t.CreatedDate).isSameOrAfter(cutoff),
        );
      }

      case '3months': {
        // Last 90 days
        const cutoff = now.clone().subtract(90, 'days');
        return tickets.filter((t) =>
          moment.utc(t.CreatedDate).isSameOrAfter(cutoff),
        );
      }

      default:
        return tickets;
    }
  }

  loadAllTicketsForCsv(): void {
    this.ticketService
      .getTickets(0, 10000, '', this.timeRange)
      .pipe(takeUntil(this.destroy$))
      .subscribe((tickets) => {
        const csvData: CSVTicket[] = tickets?.data?.map((ticket) => {
          const businessPurpose = ticket.Business_Purpose__c?.length
            ? ticket.Business_Purpose__c
            : ticket.RecordType.Name;

          return {
            'Ticket Number': ticket.CaseNumber,
            'Business Purpose': businessPurpose,
            Title: ticket.Subject,
            Status: ticket.Status,
            Location: ticket.Service_Location_Address__c,
            Service: ticket.Service__r?.Name || null,
            'Maintenance Date':
              ticket.Maintenance_Date__c != null
                ? moment(ticket.Maintenance_Date__c).format('L')
                : null,
          };
        });

        this.ticketCsvData = csvData;
        this.cdr.detectChanges();
      });
  }
}
